type CacheEntry<T> = {
  data: T;
  expiresAt: number;
};

const cachedResponses = new Map<string, CacheEntry<unknown>>();
const pendingRequests = new Map<string, Promise<unknown>>();

const toParamPairs = (params: Record<string, unknown>) =>
  Object.entries(params)
    .filter(([, value]) => value !== undefined && value !== null && value !== '')
    .sort(([left], [right]) => left.localeCompare(right))
    .map(([key, value]) => [key, String(value)] as const);

export const buildRequestCacheKey = (url: string, params?: Record<string, unknown>) => {
  if (!params) {
    return url;
  }
  const searchParams = new URLSearchParams(toParamPairs(params));
  const query = searchParams.toString();
  return query ? `${url}?${query}` : url;
};

export const fetchWithTtlCache = async <T>(
  key: string,
  ttlMs: number,
  fetcher: () => Promise<T>,
  now: () => number = Date.now
): Promise<T> => {
  const cached = cachedResponses.get(key) as CacheEntry<T> | undefined;
  if (cached && cached.expiresAt > now()) {
    return cached.data;
  }

  const pending = pendingRequests.get(key) as Promise<T> | undefined;
  if (pending) {
    return pending;
  }

  const request = fetcher().then((data) => {
    cachedResponses.set(key, {
      data,
      expiresAt: now() + ttlMs,
    });
    return data;
  }).finally(() => {
    pendingRequests.delete(key);
  });

  pendingRequests.set(key, request);
  return request;
};

export const clearRequestCacheByPrefix = (prefix: string) => {
  for (const key of cachedResponses.keys()) {
    if (key.startsWith(prefix)) {
      cachedResponses.delete(key);
    }
  }
};
